import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Send, Info, Ticket, Calendar, MapPin } from 'lucide-react';
import { eventsAPI } from '../lib/api';
import { CATEGORY_LABELS as BASE_CATEGORY_LABELS } from '../lib/eventMeta';

const CATEGORY_LABELS = { all: 'Event', ...BASE_CATEGORY_LABELS };

function formatEventDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function FeedSlide({ event, liked, onToggleLike }) {
  const navigate = useNavigate();
  const minPrice = event.ticket_types?.length
    ? Math.min(...event.ticket_types.map(t => t.price))
    : null;

  const onShare = async () => {
    const url = `${window.location.origin}/events/${event.event_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: event.title, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); } catch {}
    }
  };

  return (
    <div className="relative w-full h-screen snap-start snap-always flex-shrink-0 bg-black overflow-hidden">
      <img
        src={event.image_url || 'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=1200&q=80'}
        alt={event.title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/90" />

      {/* Right action rail */}
      <div className="absolute right-4 md:right-10 bottom-40 flex flex-col items-center gap-6 z-10">
        <button onClick={onToggleLike} className="flex flex-col items-center gap-1 text-white">
          <Heart size={30} fill={liked ? '#E63946' : 'none'} color={liked ? '#E63946' : '#fff'} />
          <span className="text-xs font-bold drop-shadow">Like</span>
        </button>
        <button onClick={onShare} className="flex flex-col items-center gap-1 text-white">
          <Send size={26} />
          <span className="text-xs font-bold drop-shadow">Share</span>
        </button>
        <button onClick={() => navigate(`/events/${event.event_id}`)} className="flex flex-col items-center gap-1 text-white">
          <Info size={28} />
          <span className="text-xs font-bold drop-shadow">Info</span>
        </button>
      </div>

      {/* Bottom info */}
      <div className="absolute left-0 right-24 bottom-10 px-5 md:px-10 z-10">
        <span className="inline-block bg-[#E63946]/85 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-3">
          {CATEGORY_LABELS[event.category] || event.category}
        </span>
        <h2 className="text-white text-2xl md:text-4xl font-black mb-3 leading-tight line-clamp-2">{event.title}</h2>
        <div className="flex items-center gap-4 text-white/90 text-sm mb-5">
          <span className="flex items-center gap-1"><Calendar size={14} />{formatEventDate(event.date)}</span>
          <span className="flex items-center gap-1"><MapPin size={14} />{event.city}</span>
        </div>
        <button
          onClick={() => navigate(`/events/${event.event_id}`)}
          className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#C1121F] transition-colors text-white font-bold px-5 py-3 rounded-full"
        >
          <Ticket size={16} />
          {minPrice === null ? 'View Event' : minPrice === 0 ? 'Free · Get Tickets' : `From €${minPrice} · Get Tickets`}
        </button>
      </div>
    </div>
  );
}

export default function FeedPage() {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [likedIds, setLikedIds] = useState({});
  const containerRef = useRef(null);

  const loadEvents = useCallback(async (pg = 1, append = false) => {
    if (append) setLoadingMore(true);
    try {
      const resp = await eventsAPI.list({ limit: 15, page: pg });
      const raw = resp.data;
      const newEvents = raw.data || raw.events || [];
      const total = raw.total ?? raw.count ?? newEvents.length;
      setEvents(prev => append ? [...prev, ...newEvents] : newEvents);
      setHasMore(newEvents.length > 0 && pg * 15 < total);
      setPage(pg);
    } catch (e) {
      console.error('Feed load error:', e?.message);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { loadEvents(1); }, [loadEvents]);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - window.innerHeight * 1.5) {
      loadEvents(page + 1, true);
    }
  };

  const toggleLike = (id) => setLikedIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="h-screen w-full overflow-y-scroll snap-y snap-mandatory bg-black"
      style={{ scrollbarWidth: 'none' }}
    >
      <div className="absolute top-6 left-0 right-0 text-center z-20 pointer-events-none">
        <span className="text-white font-black uppercase tracking-widest text-sm drop-shadow">Discover</span>
      </div>
      {events.map(ev => (
        <FeedSlide
          key={ev.event_id}
          event={ev}
          liked={!!likedIds[ev.event_id]}
          onToggleLike={() => toggleLike(ev.event_id)}
        />
      ))}
      {events.length === 0 && (
        <div className="h-screen flex items-center justify-center text-white/60">Loading events…</div>
      )}
    </div>
  );
}
